"use client";

import { useState } from "react";
import { CheckCircle2, Clock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Car } from "@/lib/types";

interface LeadFormProps {
  car: Car;
}

export default function LeadForm({ car }: LeadFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    vat_status: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const inquiry = {
      id: `local-${Date.now()}`,
      customer_name: form.name,
      phone: form.phone,
      email: form.email,
      car_name: `${car.brand} ${car.model} ${car.variant}`,
      date: new Date().toISOString().slice(0, 10),
      status: "nova",
      message: form.message,
    };
    try {
      const existing = JSON.parse(localStorage.getItem("poptavky") ?? "[]");
      localStorage.setItem("poptavky", JSON.stringify([inquiry, ...existing]));
    } catch {}
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="text-center py-10 px-4">
        <div className="flex justify-center mb-4">
          <CheckCircle2 className="h-14 w-14 text-emerald-500" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Poptávka odeslána!</h3>
        <p className="text-gray-500 text-sm">
          Partner <strong>{car.partner.name}</strong> vás brzy kontaktuje.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-6 text-xs text-[#1a6ef5] hover:underline"
        >
          Odeslat další poptávku
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-base font-bold text-gray-900">Mám zájem o toto auto</h2>

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Jméno a příjmení *</label>
        <Input
          required
          placeholder="Jan Novák"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="h-10 text-sm"
        />
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Telefon *</label>
        <Input
          required
          type="tel"
          placeholder="+420 777 123 456"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="h-10 text-sm"
        />
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">E-mail *</label>
        <Input
          required
          type="email"
          placeholder="jan.novak@example.cz"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="h-10 text-sm"
        />
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Typ subjektu *</label>
        <Select
          required
          value={form.vat_status}
          onValueChange={(v) => setForm({ ...form, vat_status: v ?? "" })}
        >
          <SelectTrigger className="h-10 text-sm">
            <SelectValue placeholder="Vyberte..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fyzicka">Fyzická osoba</SelectItem>
            <SelectItem value="osvc">OSVČ</SelectItem>
            <SelectItem value="firma_dph">Firma — plátce DPH</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Zpráva (volitelné)</label>
        <textarea
          rows={3}
          placeholder="Prosím o kontakt dopoledne..."
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
        />
      </div>

      <Button
        type="submit"
        className="w-full bg-[#1a6ef5] hover:bg-[#155dd4] text-white h-11 text-sm font-semibold"
      >
        Odeslat poptávku
      </Button>

      <div className="flex flex-col gap-1.5 pt-1">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          <span>Partner vás kontaktuje do 24 hodin</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
          <span>Bez závazků · Zdarma</span>
        </div>
      </div>
    </form>
  );
}
